import { signInWithCredential, OAuthProvider } from 'firebase/auth';
import { log } from '@/logger/logger.ts';
import { generateRandomString, sha256 } from '@/utils/auth.utils';
import { getAxiosInstance } from '@/services/axiosConfig';
import { auth } from '../../../firebase';
import { figma as figmaOidcConfig } from '../oidc-provider-configs';
import { INTERNAL_AUTH_ERROR_CODES, InternalAuthError } from '../firebase-errors';
import type { SignInResult } from '../../authProvider.types';

const axiosInstance = getAxiosInstance();
const logger = log.getLogger('figma-oidc-provider');
const PREFIX = 'weavy_figma_signin';
const FIGMA_OIDC_ACCESS_TOKEN_KEY = 'figma_access_token';
/**
 * Fetches Figma ID token from server by exchanging authorization code
 */
interface FigmaTokenResponse {
  id_token: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user_id_string?: string;
}

const fetchFigmaToken = async (code: string): Promise<FigmaTokenResponse> => {
  try {
    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    const response = await axiosInstance.post<FigmaTokenResponse>(`/v1/auth/figma/oidc/token`, { code, redirectUri });
    if (!response.data || !response.data.id_token) {
      throw new Error('No id_token received from server');
    }

    return response.data;
  } catch (error) {
    logger.error('Error fetching Figma token:', error);
    throw error;
  }
};

let pendingFigmaTokenResponse: FigmaTokenResponse | null = null;

export const clearPendingFigmaTokens = (): void => {
  pendingFigmaTokenResponse = null;
};

/** Stores pending Figma tokens server-side. Call after user record is created. */
export const storePendingFigmaTokens = async (): Promise<void> => {
  const tokenResponse = pendingFigmaTokenResponse;
  pendingFigmaTokenResponse = null;
  if (tokenResponse) {
    await storeTokensServerSide(tokenResponse);
  }
};

const storeTokensServerSide = async (tokenResponse: FigmaTokenResponse): Promise<void> => {
  if (
    !tokenResponse.access_token ||
    !tokenResponse.refresh_token ||
    !tokenResponse.user_id_string ||
    !tokenResponse.expires_in
  ) {
    return;
  }
  try {
    await axiosInstance.post('/v1/auth/figma/store-tokens', {
      accessToken: tokenResponse.access_token,
      refreshToken: tokenResponse.refresh_token,
      expiresIn: tokenResponse.expires_in,
      figmaUserId: tokenResponse.user_id_string,
    });
    logger.info('Figma tokens stored server-side');
  } catch (error) {
    logger.error('Failed to store Figma tokens server-side:', error);
  }
};

/**
 * Initiates Figma OAuth redirect flow
 */
export const initiateFigmaRedirectSignIn = async (redirectPath: string): Promise<void> => {
  const redirectUri = encodeURIComponent(`${window.location.origin}${redirectPath}`);
  const clientId = figmaOidcConfig.clientId;
  const baseUrl = figmaOidcConfig.baseUrl;
  const state = generateRandomString(32);
  const rawNonce = generateRandomString(32);
  const hashedNonce = await sha256(rawNonce);

  // Store auth state
  localStorage.setItem(`${PREFIX}_nonce`, rawNonce);
  localStorage.setItem(`${PREFIX}_state`, state);

  // Store promotion query param if it exists, clear it if it doesn't
  const urlParams = new URLSearchParams(window.location.search);
  const promotion = urlParams.get('promotion');
  if (promotion) {
    localStorage.setItem(`${PREFIX}_sign_in_promotion`, promotion);
  } else {
    localStorage.removeItem(`${PREFIX}_sign_in_promotion`);
  }

  const fuid = urlParams.get('fuid');

  const scope = 'openid profile email file_content:read';
  const fuidParam = fuid ? `&fuid=${encodeURIComponent(fuid)}` : '';
  const oauthUrl = `${baseUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&nonce=${hashedNonce}${fuidParam}`;

  // Redirect the user to the authorization URL
  window.location.href = oauthUrl;
};

const CONNECT_PREFIX = 'weavy_figma_connect';

/**
 * Initiates Figma OAuth redirect for linking a Figma account (not sign-in).
 * Redirects directly to /settings, which is registered as a valid callback URL.
 */
export const initiateFigmaConnectRedirect = async (): Promise<void> => {
  const redirectPath = '/settings';
  const redirectUri = encodeURIComponent(`${window.location.origin}${redirectPath}`);
  const clientId = figmaOidcConfig.clientId;
  const baseUrl = figmaOidcConfig.baseUrl;
  const state = generateRandomString(32);
  const rawNonce = generateRandomString(32);
  const hashedNonce = await sha256(rawNonce);

  localStorage.setItem(`${CONNECT_PREFIX}_state`, state);
  localStorage.setItem(`${CONNECT_PREFIX}_redirect_uri`, `${window.location.origin}${redirectPath}`);

  const scope = 'openid profile email file_content:read';
  const oauthUrl = `${baseUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&nonce=${hashedNonce}`;

  window.location.href = oauthUrl;
};

/**
 * Retrieves and clears the stored promotion parameter
 */
export const getStoredPromotion = (): string | null => {
  const promotion = localStorage.getItem(`${PREFIX}_sign_in_promotion`);
  localStorage.removeItem(`${PREFIX}_sign_in_promotion`);
  return promotion;
};

/**
 * Signs in with Figma using authorization code
 */
export const signInWithAuthorizationCode = async (authorizationCode: string): Promise<SignInResult> => {
  if (!authorizationCode) {
    throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_CODE, 'Figma authorization code is required');
  }

  const state = localStorage.getItem(`${PREFIX}_state`);
  const urlState = new URLSearchParams(window.location.search).get('state');
  localStorage.removeItem(`${PREFIX}_state`);
  if (!state || !urlState) {
    throw new InternalAuthError(
      INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_STATE,
      'State is missing from the session storage or url',
    );
  }
  if (state !== urlState) {
    throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.OIDC_STATE_MISMATCH, 'State parameter mismatch');
  }

  const nonce = localStorage.getItem(`${PREFIX}_nonce`);
  localStorage.removeItem(`${PREFIX}_nonce`);
  if (!nonce) {
    throw new InternalAuthError(
      INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_NONCE,
      'Nonce is missing from the session storage',
    );
  }
  try {
    const tokenResponse = await fetchFigmaToken(authorizationCode);
    if (tokenResponse.access_token) {
      localStorage.setItem(FIGMA_OIDC_ACCESS_TOKEN_KEY, tokenResponse.access_token);
    }
    // Create OAuth provider and credential, then sign in
    const figmaProvider = new OAuthProvider(figmaOidcConfig.oidcProvider);
    const credential = figmaProvider.credential({
      idToken: tokenResponse.id_token,
      rawNonce: nonce || '',
    });

    const result = await signInWithCredential(auth, credential);

    // Save for server-side storage after user record is created
    pendingFigmaTokenResponse = tokenResponse;

    return result as SignInResult;
  } catch (error: any) {
    throw new InternalAuthError(
      INTERNAL_AUTH_ERROR_CODES.OIDC_SIGN_IN_WITH_FIGMA_TOKEN_FAILED,
      `Error signing in with Figma token, original error: ${JSON.stringify(error)}`,
    );
  }
};

export const getOidcAccessToken = (): string | null => {
  return localStorage.getItem(FIGMA_OIDC_ACCESS_TOKEN_KEY);
};

export const clearOidcAccessToken = (): void => {
  localStorage.removeItem(FIGMA_OIDC_ACCESS_TOKEN_KEY);
};

/**
 * Clears localStorage token if the server already has the connection.
 * Called once after user authentication to clean up migrated tokens.
 */
export const cleanupLocalStorageTokenIfMigrated = async (): Promise<void> => {
  const localToken = localStorage.getItem(FIGMA_OIDC_ACCESS_TOKEN_KEY);
  if (!localToken) return;

  try {
    const { data } = await axiosInstance.get<{ connected: boolean }>('/v1/auth/figma/connections');
    if (data.connected) {
      localStorage.removeItem(FIGMA_OIDC_ACCESS_TOKEN_KEY);
      logger.info('Cleared localStorage Figma token — server-side tokens are active');
    }
  } catch (error) {
    logger.error('Failed to check Figma connection status:', error);
  }
};
